import type { ClientErrorApp } from '@dcs/contracts';
import { createErrorReporter } from './reporter.ts';
import type { ErrorReporterPort, ErrorSend } from './reporter.ts';

/**
 * Browser binding of the error reporter. Everything is read from `globalThis` loosely (this package has
 * no DOM lib); a missing API means "not supported" and reporting degrades to a no-op.
 */

type Loose = Record<string, unknown>;
type Method = (...args: unknown[]) => unknown;

function read(source: unknown, key: string): unknown {
  try {
    return typeof source === 'object' && source !== null ? (source as Loose)[key] : undefined;
  } catch {
    return undefined;
  }
}

function method(owner: unknown, name: string): Method | null {
  const value = read(owner, name);
  return typeof value === 'function' ? (value as Method) : null;
}

/**
 * `fetch` with `keepalive` first (survives page unload, and `credentials: 'omit'` keeps the session
 * cookie off a report that does not need it); `navigator.sendBeacon` only where `fetch` is missing or
 * throws (a beacon always carries cookies).
 */
export function browserErrorSend(): ErrorSend {
  return (url, body) => {
    try {
      const pending = method(globalThis, 'fetch')?.call(globalThis, url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      });
      if (pending instanceof Promise) {
        pending.catch(() => undefined);
        return;
      }
    } catch {
      // Fall back to a beacon.
    }
    try {
      const nav = read(globalThis, 'navigator');
      const BlobCtor = read(globalThis, 'Blob') as
        (new (parts: string[], init: object) => unknown) | undefined;
      if (BlobCtor !== undefined) {
        method(nav, 'sendBeacon')?.call(
          nav,
          url,
          new BlobCtor([body], { type: 'application/json' }),
        );
      }
    } catch {
      // Nothing more to try.
    }
  };
}

export interface WebErrorReporterOptions {
  app: ClientErrorApp;
  /** Default: `/api/client-errors` (same origin: the Pages Function forwards `/api/*`). */
  endpoint?: string;
}

/** A reporter bound to the browser: current path, `fetch` keepalive, `navigator.onLine` respected. */
export function createWebErrorReporter(options: WebErrorReporterOptions): ErrorReporterPort {
  const nav = (): unknown => read(globalThis, 'navigator');
  return createErrorReporter({
    app: options.app,
    endpoint: options.endpoint ?? '/api/client-errors',
    route: () => {
      const pathname = read(read(globalThis, 'location'), 'pathname');
      return typeof pathname === 'string' ? pathname : '/';
    },
    now: () => Date.now(),
    send: browserErrorSend(),
    online: () => read(nav(), 'onLine') !== false,
  });
}

/** What a `window` `error` event carries. */
interface ErrorEventLike {
  error?: unknown;
  message?: unknown;
}

/**
 * Reports uncaught errors and unhandled promise rejections of the page. Returns a function that removes
 * the listeners. A script error from another origin has no `error` (only "Script error."): it is
 * dropped, there is nothing to group on.
 */
export function installGlobalErrorHandlers(reporter: ErrorReporterPort): () => void {
  const win: unknown = globalThis;
  const add = method(win, 'addEventListener');
  const remove = method(win, 'removeEventListener');
  if (add === null) return () => undefined;

  const onError = (event: unknown) => {
    const { error, message } = (event ?? {}) as ErrorEventLike;
    if (error !== undefined && error !== null) reporter.report(error, { source: 'window' });
    else if (typeof message === 'string' && message !== 'Script error.') {
      reporter.report(message, { source: 'window' });
    }
  };
  const onRejection = (event: unknown) => {
    reporter.report(read(event, 'reason'), { source: 'unhandledrejection' });
  };
  add.call(win, 'error', onError);
  add.call(win, 'unhandledrejection', onRejection);
  return () => {
    remove?.call(win, 'error', onError);
    remove?.call(win, 'unhandledrejection', onRejection);
  };
}
